import type { BehaviorAnalysisEntry, ClassPerformance, RevenueAnalysis, SubjectPerformance } from "../../services/analyticsService";
import { byNumber, byText, reversed, type RegisterConfig } from "../register/registerLogic";

/** Tables beside the Analytics charts, and the change figures on its headline tiles. */

export const num = (value: unknown) => (value === null || value === undefined || value === "" ? null : Number(value));

/**
 * The change from the span before, in words a head teacher reads at a glance.
 * `points` for percentages (a rise from 60% to 63% is 3 points, not 5%).
 */
export function change(now: unknown, before: unknown, kind: "points" | "percent" | "count") {
  const current = num(now);
  const previous = num(before);
  if (current === null || previous === null) return null;
  const difference = current - previous;
  if (kind === "points") return { difference, text: `${difference >= 0 ? "+" : "−"}${Math.abs(difference).toFixed(1)} pts` };
  if (kind === "count") return { difference, text: `${difference >= 0 ? "+" : "−"}${Math.abs(difference).toLocaleString()}` };
  if (!previous) return current ? { difference, text: "new" } : { difference: 0, text: "no change" };
  const percent = (difference / previous) * 100;
  return { difference, text: `${percent >= 0 ? "+" : "−"}${Math.abs(percent).toFixed(1)}%` };
}

const score = (value: unknown) => num(value) ?? -1;

export const classConfig: RegisterConfig<ClassPerformance> = {
  search: (row) => row.class_name,
  views: [
    { key: "all", label: "All" },
    { key: "assessed", label: "With results", match: (row) => row.results > 0 },
    { key: "below", label: "Average below 50%", match: (row) => (num(row.average_score) ?? 100) < 50 },
    { key: "none", label: "No results yet", match: (row) => row.results === 0 },
  ],
  sorts: [
    { key: "order", label: "School order", compare: () => 0 },
    { key: "-average", label: "Average (highest first)", compare: reversed(byNumber<ClassPerformance>((row) => score(row.average_score))) },
    { key: "average", label: "Average (lowest first)", compare: byNumber<ClassPerformance>((row) => score(row.average_score)) },
    { key: "-attendance", label: "Attendance (highest first)", compare: reversed(byNumber<ClassPerformance>((row) => score(row.attendance_rate))) },
  ],
};

export const subjectConfig: RegisterConfig<SubjectPerformance> = {
  search: (row) => row.subject_name,
  views: [
    { key: "all", label: "All" },
    { key: "below", label: "Average below 50%", match: (row) => Number(row.average_score) < 50 },
    { key: "falling", label: "Lower than before", match: (row) => row.previous_average_score !== null && Number(row.average_score) < Number(row.previous_average_score) },
    { key: "rising", label: "Higher than before", match: (row) => row.previous_average_score !== null && Number(row.average_score) > Number(row.previous_average_score) },
  ],
  sorts: [
    { key: "-average", label: "Average (highest first)", compare: reversed(byNumber<SubjectPerformance>((row) => row.average_score)) },
    { key: "average", label: "Average (lowest first)", compare: byNumber<SubjectPerformance>((row) => row.average_score) },
    { key: "-pass", label: "Pass rate (highest first)", compare: reversed(byNumber<SubjectPerformance>((row) => score(row.pass_rate))) },
    { key: "name", label: "Subject (A–Z)", compare: byText<SubjectPerformance>((row) => row.subject_name) },
  ],
};

export const REVENUE_SOURCES = [
  { key: "fees", label: "Fees" },
  { key: "transport", label: "Transport" },
  { key: "boarding", label: "Boarding" },
  { key: "cafeteria", label: "Meals" },
  { key: "uniforms", label: "Uniforms" },
  { key: "other", label: "Other" },
] as const;

export const revenueConfig: RegisterConfig<RevenueAnalysis> = {
  search: (row) => row.period,
  views: [
    { key: "all", label: "All months" },
    { key: "money", label: "With receipts", match: (row) => Number(row.total) > 0 },
  ],
  sorts: [
    { key: "-period", label: "Newest first", compare: reversed(byText<RevenueAnalysis>((row) => row.period)) },
    { key: "period", label: "Oldest first", compare: byText<RevenueAnalysis>((row) => row.period) },
    { key: "-total", label: "Total (highest first)", compare: reversed(byNumber<RevenueAnalysis>((row) => row.total as string)) },
  ],
};

export const behaviourConfig: RegisterConfig<BehaviorAnalysisEntry> = {
  search: (row) => row.incident_type,
  views: [
    { key: "all", label: "All" },
    { key: "declining", label: "More than before", match: (row) => row.trend === "declining" },
    { key: "improving", label: "Fewer than before", match: (row) => row.trend === "improving" },
  ],
  sorts: [
    { key: "-count", label: "Most incidents first", compare: reversed(byNumber<BehaviorAnalysisEntry>((row) => row.count)) },
    { key: "name", label: "Category (A–Z)", compare: byText<BehaviorAnalysisEntry>((row) => row.incident_type) },
  ],
};

/** "2026-09" → "Sept 2026". */
export const monthLabel = (period: string) =>
  new Date(`${period}-01T00:00:00`).toLocaleDateString(undefined, { month: "short", year: "numeric" });
